import React from 'react';

// Memoized Input Field to prevent re-renders
export const FormInput = React.memo(({ label, name, value, onChange, type = 'text', required, error, disabled, ...props }) => (
  <div>
    <label className="block text-xs font-medium mb-1 text-gray-600">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      className={`w-full p-2 border rounded text-sm ${error ? 'border-red-500' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
      {...props}
    />
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
));

// Memoized Date Field
export const FormDate = React.memo(({ label, name, value, onChange, required, error, disabled }) => (
  <div>
    <label className="block text-xs font-medium mb-1 text-gray-600">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="date"
      className={`w-full p-2 border rounded text-sm ${error ? 'border-red-500' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      value={value || ''}
      onChange={onChange}
      min="1900-01-01"
      max="2100-12-31"
      disabled={disabled}
    />
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
));

// Memoized Select Field
export const FormSelect = React.memo(({ label, name, value, onChange, options, required, disabled }) => (
  <div>
    <label className="block text-xs font-medium mb-1 text-gray-600">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      className={`w-full p-2 border rounded text-sm ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
    >
      {options.map((opt, i) => (
        <option key={i} value={typeof opt === 'object' ? opt.value : opt}>
          {typeof opt === 'object' ? opt.label : opt}
        </option>
      ))}
    </select>
  </div>
));

// Tab definitions with keyboard shortcuts
export const bookingTabs = [
  { id: 'customer', label: 'Customer', shortcut: 'F1', color: 'bg-pink-500' },
  { id: 'vehicle', label: 'Vehicle', shortcut: 'F2', color: 'bg-blue-500' },
  { id: 'insurance', label: 'Insurance', shortcut: 'F3', color: 'bg-purple-500' },
  { id: 'registration', label: 'Registration', shortcut: 'F4', color: 'bg-green-500' },
  { id: 'accessories', label: 'Accessories', shortcut: 'F5', color: 'bg-yellow-500' },
  { id: 'accounting', label: 'Accounting', shortcut: 'F6', color: 'bg-orange-500' },
  { id: 'exchange', label: 'Exchange', shortcut: 'F7', color: 'bg-teal-500' },
  { id: 'booking', label: 'Booking', shortcut: 'F8', color: 'bg-indigo-500' },
  { id: 'finance', label: 'Finance', shortcut: 'F9', color: 'bg-rose-500' },
  { id: 'pme', label: 'PME Drive', shortcut: 'F10', color: 'bg-cyan-500' },
  { id: 'sellers', label: 'Sellers', shortcut: 'F11', color: 'bg-lime-600' },
  { id: 'agents', label: 'Agents', shortcut: 'F12', color: 'bg-amber-500' },
  { id: 'vendors', label: 'Vendors', shortcut: '', color: 'bg-emerald-500' },
];

// Horizontal Tab navigation component (legacy - for backward compatibility)
export const TabNavigation = React.memo(({ tabs, activeTab, setActiveTab }) => (
  <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
    {tabs.map(tab => (
      <button
        key={tab.id}
        type="button"
        onClick={() => setActiveTab(tab.id)}
        className={`px-4 py-2 text-sm font-medium whitespace-nowrap rounded-lg transition-all ${
          activeTab === tab.id
            ? `${tab.color} text-white shadow-md`
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
));

// Vertical Tab navigation component (Tally-style)
export const VerticalTabNavigation = React.memo(({ tabs, activeTab, setActiveTab }) => (
  <div className="flex flex-col gap-1">
    {tabs.map(tab => (
      <button
        key={tab.id}
        type="button"
        onClick={() => setActiveTab(tab.id)}
        className={`px-3 py-2.5 text-left text-sm font-medium rounded-lg transition-all flex justify-between items-center ${
          activeTab === tab.id
            ? `${tab.color} text-white shadow-md`
            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
        }`}
      >
        <span>{tab.label}</span>
        {tab.shortcut && (
          <span className={`text-xs ${activeTab === tab.id ? 'text-white/70' : 'text-gray-400'}`}>
            {tab.shortcut}
          </span>
        )}
      </button>
    ))}
  </div>
));
